import OpenModal from 'app/components/VideoModal/OpenModal';
import CONTENTO_TYPES from 'app/domain/ContentoTypes';
import { ContentItemFragment, Presentation } from 'app/graphql/__generated__/types/contento';
import { getContentSvtId } from 'app/utils/contentoUtils';
import Link from 'next/link';
import React, { useState } from 'react';
import ContinueWatching from 'app/utils/models/ContinueWatching';
import AccessServiceTypes from '../../domain/AccessServiceTypes';
import useTrackImpression, { OnItemImpressionType } from '../../utils/useTrackImpression';
import ContentItemFigure from './components/ContentItemFigure/ContentItemFigure';
import ContentItemInfo from './components/ContentItemInfo/ContentItemInfo';
import { ContentItemLink, ContentItemWrapper, StyledProgressBar } from './ContentItem.styles';
import { getHeading, getSubHeading, getUrl } from './helpers';

type LinkTypeResolverProps = {
    autoPlay: boolean;
    href: string;
    children?: (handler: () => void) => React.ReactNode;
    videoId: string;
};

const LinkTypeResolver = ({ autoPlay, href, children, videoId }: LinkTypeResolverProps) => {
    if (autoPlay) {
        return (
            <OpenModal videoUrl={href} videoId={videoId}>
                {(handleOpenModal) => children(handleOpenModal)}
            </OpenModal>
        );
    }

    return (
        <Link href={href} passHref>
            {children(null)}
        </Link>
    );
};

export type Props = {
    content: ContentItemFragment;
    index: number;
    className?: string;
    loadImage?: boolean;
    listId?: string;
    editModeActivated?: boolean;
    markedAsDeleted?: boolean;
    presentation?: Presentation;
    preferredAccessVersion?: AccessServiceTypes;
    progress?: number;
    onMarkedAsDeleted?: (markedAsDeletedObject: ContinueWatching) => void;
    onClick?: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        itemPosition: number,
        svtId: string,
        autoPlay: boolean,
        b: boolean
    ) => void;
    onItemImpression: OnItemImpressionType;
    itemPosition?: number;
    forwardedRef?: React.RefObject<any> | ((node?: Element) => void);
    inView?: boolean;
    autoPlay?: boolean;
    playingNow?: boolean;
    linkToDetailsPage?: boolean;
};

const ContentItem = ({
    className,
    index,
    content,
    loadImage = true,
    listId,
    editModeActivated,
    markedAsDeleted,
    presentation,
    preferredAccessVersion,
    progress,
    onMarkedAsDeleted,
    onClick,
    onItemImpression,
    itemPosition,
    forwardedRef,
    inView,
    autoPlay = false,
    playingNow = false,
    linkToDetailsPage = true,
}: Props): JSX.Element => {
    const [expanded, toggleExpanded] = useState<boolean>(false);
    const [expansionIconVisible, toggleExpansionIconVisible] = useState<boolean>(false);
    const [deletedItem, toggleDeletedItem] = useState<boolean>(markedAsDeleted);
    const [trackingRef] = useTrackImpression(
        onItemImpression,
        itemPosition,
        getContentSvtId(content.item, preferredAccessVersion)
    );

    const url = getUrl(content, preferredAccessVersion);

    const inGrid = typeof inView === 'boolean';
    const shouldRender = inView || !inGrid;
    const clipsShouldAutoPlay = [CONTENTO_TYPES.CLIP, CONTENTO_TYPES.TRAILER].includes(
        content.item.__typename as CONTENTO_TYPES
    ); // A/B-test autoplay clips nov 2021
    const autoPlayOverlay = (autoPlay || clipsShouldAutoPlay) && !editModeActivated;

    const onItemClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (editModeActivated) {
            e.preventDefault();
            if (!deletedItem) {
                toggleDeletedItem(true);
                onMarkedAsDeleted({
                    videoId: content.item.svtId,
                    titleArticleId: content.item?.parent?.svtId,
                    svtId: content.item?.parent?.svtId,
                });
            }
        }
        if (onClick) {
            onClick(
                e,
                itemPosition,
                getContentSvtId(content.item, preferredAccessVersion),
                autoPlayOverlay,
                true
            );
        }
    };

    const onContentItemInfoClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (onClick) {
            onClick(e, itemPosition, getContentSvtId(content.item, preferredAccessVersion), false, false);
        }
    };

    const onFocus = () => {
        toggleExpansionIconVisible(true);
    };

    const heading = getHeading(content);

    return (
        <ContentItemWrapper
            className={className}
            ref={forwardedRef}
            id={playingNow ? 'episode-picker-playing-now' : undefined}
        >
            {/* Only render if content item is in view (intersection observer) */}
            {shouldRender && (
                <>
                    <LinkTypeResolver href={url} autoPlay={autoPlayOverlay} videoId={content.item.svtId}>
                        {(inheritedClickHandler) => (
                            <ContentItemLink
                                aria-label={
                                    autoPlay || clipsShouldAutoPlay ? `${heading} - starta video` : heading
                                }
                                tabIndex={0}
                                onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                    inheritedClickHandler && inheritedClickHandler();
                                    onItemClick(e);
                                }}
                                onFocus={onFocus}
                                role={autoPlayOverlay ? 'button' : 'link'}
                                className={`lp_pos${index}`}
                                ref={trackingRef}
                            >
                                <ContentItemFigure
                                    content={content}
                                    showPlaceholder={!loadImage}
                                    presentation={presentation}
                                    autoPlayOverlay={autoPlayOverlay}
                                    editModeActivated={editModeActivated}
                                    markedAsDeleted={deletedItem}
                                    playingNow={playingNow}
                                >
                                    {!deletedItem && <StyledProgressBar selected progress={progress} />}
                                </ContentItemFigure>
                            </ContentItemLink>
                        )}
                    </LinkTypeResolver>

                    <ContentItemInfo
                        heading={heading}
                        subHeading={getSubHeading(listId, content)}
                        longDescription={content.item?.longDescription}
                        url={url}
                        onContentInfoClick={onContentItemInfoClick}
                        editModeActivated={false}
                        index={index}
                        expanded={expanded}
                        toggleExpanded={toggleExpanded}
                        expansionIconVisible={expansionIconVisible}
                        toggleExpansionIconVisible={toggleExpansionIconVisible}
                        linkToDetailsPage={linkToDetailsPage}
                    />
                </>
            )}
        </ContentItemWrapper>
    );
};

export default React.memo(ContentItem);
